import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  SkipForward,
  Upload,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { AdminHeader, BulkUploadTabs } from './adminAccess'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { ImportStreamLine, StudentImportOutcome, StudentImportResponse, StudentImportRowResult } from '../../types/api'

/** How many failed or skipped rows are listed live; the final report lists them all. */
const MAX_LIVE_ISSUES = 200

/** Matches spring.servlet.multipart.max-file-size, so the two cannot disagree silently. */
const MAX_UPLOAD_BYTES = 64 * 1024 * 1024

/** KB up to a megabyte, MB after. "22100.2 KB" is a number nobody reads as 22 MB. */
function formatSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/** How each outcome is coloured and named in the results table. */
const OUTCOME: Record<StudentImportOutcome, { label: string; className: string }> = {
  CREATED: { label: 'Created', className: 'bg-green-100 text-green-800' },
  WOULD_CREATE: { label: 'Ready', className: 'bg-sky-100 text-sky-800' },
  SKIPPED: { label: 'Skipped', className: 'bg-amber-100 text-amber-800' },
  FAILED: { label: 'Failed', className: 'bg-red-100 text-red-800' },
}

/**
 * What one bulk sign-up page is, beyond the half that every one of them shares.
 *
 * The upload flow, the two-step check-then-import gating, the size guard, the results table and
 * the ops header are identical whichever side of the marketplace is being created - so they live
 * here once, and a page is this much configuration. The backend is arranged the same way, with one
 * import engine and an ImportRole that carries the difference.
 */
export type BulkImportConfig = {
  title: string
  intro: string
  /** Lower case, singular - used in the results table heading. */
  noun: string
  /** Lower case, plural - used in the "up to 50,000" hint. */
  nounPlural: string
  /** What the downloaded template is saved as. */
  templateFilename: string
  /** The column list for the right-hand panel. */
  columns: React.ReactNode
  /** The amber box under it: what an admin should know before creating these accounts. */
  notice: React.ReactNode
  /** `sendInvites` is only ever true where the panel offers the "email everyone" box. */
  upload: (
    file: File,
    dryRun: boolean,
    onLine: (line: ImportStreamLine) => void,
    sendInvites?: boolean,
  ) => Promise<void>
  downloadTemplate: () => Promise<Blob>
}

/** What {@link BulkImportPanel} needs - the page config without the ops page's own heading and nav. */
export type BulkImportPanelConfig = Omit<BulkImportConfig, 'title' | 'intro'>


/** Running counts while an import streams in - see {@link BulkImportPage.run}. */
type Progress = {
  /** Null until the stream's first line arrives, which is what carries it. */
  total: number | null
  processed: number
  created: number
  skipped: number
  failed: number
}

/**
 * The upload half of a bulk sign-up page - file picker, streamed progress, results table and the
 * column guide - without the ops chrome, so an aggregator's own dashboard can run the same import.
 *
 * @param onUnauthorized what a 401/403 means to the page around it. The ops page signs out; a page
 *   that leaves it unset shows the server's message instead, which is how an aggregator awaiting
 *   approval is told why its upload was refused.
 */
export function BulkImportPanel({
  config,
  onUnauthorized,
  onFinished,
  inviteFrom = 'the Report status tab',
  offerSendNow = false,
}: {
  config: BulkImportPanelConfig
  onUnauthorized?: () => void
  /** Called once an import has run to the end, so the page around it can refresh its own counts. */
  onFinished?: () => void
  /** Where the person uploading picks who to email - ops' Report status tab, or a coordinator's own list. */
  inviteFrom?: string
  /** Show the "email everyone now" box - ops only; a coordinator always sends from their own list. */
  offerSendNow?: boolean
}) {
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<StudentImportResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<Progress | null>(null)
  const [sendNow, setSendNow] = useState(false)
  // Failed and skipped rows as they stream in, with their reasons - so a coordinator watching a long
  // import sees why a row was refused at once, not only in the report at the end. Few by nature.
  const [issues, setIssues] = useState<StudentImportRowResult[]>([])
  // Every row lands here as it streams in, not in state: with up to 50,000 of them, re-rendering
  // the full results table on every single row would be the actual bottleneck, not the import
  // itself. Only the small Progress object re-renders live; this becomes the final report's rows
  // once the stream ends.
  const rowsRef = useRef<StudentImportRowResult[]>([])

  function pick(next: File | null) {
    setFile(next)
    // The old report belongs to the old file, and leaving it on screen beside a new filename is
    // the one way to read this page wrong.
    setResult(null)
    // Caught here rather than after the upload: sending 64 MB only to be told it was too big
    // wastes minutes on a slow connection, and the failure arrives looking like a dead server.
    if (next && next.size > MAX_UPLOAD_BYTES) {
      setError(
        `That file is ${(next.size / 1024 / 1024).toFixed(1)} MB. The limit is ${
          MAX_UPLOAD_BYTES / 1024 / 1024
        } MB - please split it.`,
      )
      return
    }
    setError(null)
  }

  async function run() {
    if (!file) return
    setBusy(true)
    setError(null)
    setResult(null)
    rowsRef.current = []
    setIssues([])
    setProgress({ total: null, processed: 0, created: 0, skipped: 0, failed: 0 })
    try {
      await config.upload(file, false, (line) => {
        if ('total' in line) {
          setProgress((p) => (p ? { ...p, total: line.total } : p))
          return
        }
        const row = line.result
        rowsRef.current.push(row)
        if (row.outcome === 'FAILED' || row.outcome === 'SKIPPED') {
          setIssues((list) => (list.length < MAX_LIVE_ISSUES ? [...list, row] : list))
        }
        const isCreated = row.outcome === 'CREATED' || row.outcome === 'WOULD_CREATE'
        setProgress((p) =>
          p
            ? {
                ...p,
                processed: p.processed + 1,
                created: p.created + (isCreated ? 1 : 0),
                skipped: p.skipped + (row.outcome === 'SKIPPED' ? 1 : 0),
                failed: p.failed + (row.outcome === 'FAILED' ? 1 : 0),
              }
            : p,
        )
      }, offerSendNow && sendNow)
      const rows = rowsRef.current
      setResult({
        dryRun: false,
        total: rows.length,
        created: rows.filter((r) => r.outcome === 'CREATED' || r.outcome === 'WOULD_CREATE').length,
        skipped: rows.filter((r) => r.outcome === 'SKIPPED').length,
        failed: rows.filter((r) => r.outcome === 'FAILED').length,
        rows,
      })
      onFinished?.()
    } catch (err) {
      if (onUnauthorized && err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        onUnauthorized()
        return
      }
      setError(apiErrorMessage(err, 'Could not read that file'))
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  async function downloadTemplate() {
    setError(null)
    try {
      const blob = await config.downloadTemplate()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = config.templateFilename
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not download the template'))
    }
  }

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <div className="rounded-xl border border-ink-200 bg-white p-5">
          <label className="block cursor-pointer rounded-lg border-2 border-dashed border-ink-200 px-4 py-8 text-center transition hover:border-sun-400 hover:bg-sun-400/5">
            <input
              type="file"
              // The server reads either, and picks by the file's extension - so this filter
              // and that choice have to list the same things.
              accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="sr-only"
              onChange={(e) => pick(e.target.files?.[0] ?? null)}
            />
            <FileSpreadsheet size={28} className="mx-auto text-ink-400" />
            <span className="mt-2 block font-semibold text-ink-900">
              {file ? file.name : 'Choose a CSV or Excel file'}
            </span>
            <span className="mt-0.5 block text-xs text-ink-500">
              {file
                ? `${formatSize(file.size)} - click to choose a different one`
                : `Up to 50,000 ${config.nounPlural} per import`}
            </span>
          </label>

          {offerSendNow && (
            <label className="mt-4 flex items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={sendNow}
                disabled={busy}
                onChange={(e) => setSendNow(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold text-ink-900">Email everyone their invitation now.</span>{' '}
                Every account this upload creates is sent its Confirm / Reject email as it is made.
                Leave it off to send them yourself from {inviteFrom}.
              </span>
            </label>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!file || busy}
              onClick={run}
              className="btn-sun gap-1.5 disabled:opacity-40"
            >
              <Upload size={15} /> {busy ? 'Working...' : 'Import'}
            </button>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            {offerSendNow && sendNow
              ? 'Every row becomes a draft and is emailed its invitation straight away.'
              : `No email is sent - every row becomes a draft; invite them from ${inviteFrom}.`}{' '}
            Cannot be
            undone from here, and takes at most 50,000 rows at a time - a file spread across many different
            pincodes can take a while, since each new one is geocoded at a throttled one a
            second. Re-uploading an overlapping file is safe, since rows that already exist are
            skipped.
          </p>

          {progress && <ProgressBar progress={progress} noun={config.nounPlural} />}
          {progress && issues.length > 0 && <LiveIssues issues={issues} />}
        </div>

        <ErrorMessage message={error} />

        {result && <ImportReport result={result} noun={config.noun} />}
      </div>

      <aside className="space-y-4">
        <div className="rounded-xl border border-ink-200 bg-white p-5 text-sm">
          <h2 className="font-bold text-ink-900">The columns</h2>
          <p className="mt-1 text-ink-500">
            The header names are matched exactly, so start from the template. A .csv, .xls or .xlsx all work - the first sheet is read.
          </p>
          <button
            type="button"
            onClick={downloadTemplate}
            className="btn-ghost mt-3 w-full justify-center gap-1.5"
          >
            <Download size={15} /> Download the template
          </button>
          <dl className="mt-4 space-y-2 text-xs">{config.columns}</dl>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          <AlertTriangle size={15} className="mb-1" />
          <p>{config.notice}</p>
        </div>
      </aside>
    </div>
  )
}

/** The ops page: the admin header around {@link BulkImportPanel}. */
export function BulkImportPage({ config }: { config: BulkImportConfig }) {
  const navigate = useNavigate()

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  return (
    <div className="admin-console min-h-screen bg-paper lg:pl-64">
      <AdminHeader current="bulkUpload" onSignOut={signOut} width="max-w-6xl" />

      <main className="mx-auto max-w-6xl px-4 py-6">
        <BulkUploadTabs />
        <h1 className="mt-5 text-2xl font-extrabold text-ink-900">{config.title}</h1>
        <p className="mt-1 text-sm text-ink-500">{config.intro}</p>
        <BulkImportPanel config={config} onUnauthorized={signOut} offerSendNow />
      </main>
    </div>
  )
}

export function Column({
  name,
  required,
  children,
}: {
  name: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <dt className="font-mono font-semibold text-ink-900">
        {name}
        {required && <span className="ml-1 text-red-500">*</span>}
      </dt>
      <dd className="text-ink-500">{children}</dd>
    </div>
  )
}

function ImportReport({ result, noun }: { result: StudentImportResponse; noun: string }) {
  // Failures and skips first: they are the only rows that need a person to do something, and in a
  // three-hundred-row file they would otherwise be scattered among the successes.
  const ordered = [...result.rows].sort((a, b) => {
    const rank = (o: StudentImportOutcome) => (o === 'FAILED' ? 0 : o === 'SKIPPED' ? 1 : 2)
    return rank(a.outcome) - rank(b.outcome) || a.line - b.line
  })

  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-bold text-ink-900">Import finished</h2>
        <span className="text-sm text-ink-500">{result.total} rows</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        <Tally
          icon={<CheckCircle2 size={14} />}
          className="bg-green-100 text-green-800"
          count={result.created}
          label="created"
        />
        <Tally
          icon={<SkipForward size={14} />}
          className="bg-amber-100 text-amber-800"
          count={result.skipped}
          label="skipped"
        />
        <Tally
          icon={<AlertTriangle size={14} />}
          className="bg-red-100 text-red-800"
          count={result.failed}
          label="failed"
        />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="border-b border-ink-200 text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="py-2 pr-3 font-semibold">Line</th>
              <th className="py-2 pr-3 font-semibold">{noun}</th>
              <th className="py-2 pr-3 font-semibold">Outcome</th>
              <th className="py-2 font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((row) => (
              <tr key={row.line} className="border-b border-ink-100 last:border-0">
                <td className="py-2 pr-3 align-top font-mono text-ink-400">{row.line}</td>
                <td className="py-2 pr-3 align-top">
                  <span className="block font-semibold text-ink-900">{row.fullName || '-'}</span>
                  <span className="block text-xs text-ink-500">{row.email || '-'}</span>
                </td>
                <td className="py-2 pr-3 align-top">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                      OUTCOME[row.outcome].className
                    }`}
                  >
                    {OUTCOME[row.outcome].label}
                  </span>
                </td>
                <td className="py-2 align-top text-ink-500">{row.message ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The rows that failed or were skipped so far, each with its reason - shown while the import runs. */
function LiveIssues({ issues }: { issues: StudentImportRowResult[] }) {
  return (
    <div className="mt-3 rounded-lg border border-red-100 bg-red-50/60 p-3">
      <div className="text-xs font-semibold text-red-900">Rows not created so far, and why</div>
      <ul className="mt-2 max-h-56 space-y-1.5 overflow-y-auto text-xs">
        {issues.map((row) => (
          <li key={row.line} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-ink-400">Line {row.line}</span>
            <span className={`rounded-full px-1.5 py-0.5 font-semibold ${OUTCOME[row.outcome].className}`}>
              {OUTCOME[row.outcome].label}
            </span>
            <span className="font-semibold text-ink-900">{row.fullName || row.email || '-'}</span>
            <span className="text-ink-600">{row.message ?? ''}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Live progress while an import streams in. The bar is indeterminate until the stream's first
 * line - the total - arrives; after that it fills against a real denominator, not a guess.
 */
function ProgressBar({ progress, noun }: { progress: Progress; noun: string }) {
  const { total, processed, created, skipped, failed } = progress
  const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : null

  return (
    <div className="mt-4 rounded-lg border border-ink-100 bg-ink-50 p-3">
      <div className="flex items-center justify-between text-xs font-semibold text-ink-700">
        <span>
          {total === null
            ? 'Reading the file...'
            : `${processed.toLocaleString()} of ${total.toLocaleString()} ${noun} processed`}
        </span>
        {pct !== null && <span>{pct}%</span>}
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ink-200">
        <div
          className={`h-full rounded-full bg-sun-500 ${pct === null ? 'animate-pulse' : 'transition-[width] duration-200'}`}
          style={{ width: pct === null ? '12%' : `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <Tally icon={<CheckCircle2 size={13} />} className="bg-green-100 text-green-800" count={created} label="created" />
        <Tally icon={<SkipForward size={13} />} className="bg-amber-100 text-amber-800" count={skipped} label="skipped" />
        <Tally icon={<AlertTriangle size={13} />} className="bg-red-100 text-red-800" count={failed} label="failed" />
      </div>
    </div>
  )
}

function Tally({
  icon,
  className,
  count,
  label,
}: {
  icon: React.ReactNode
  className: string
  count: number
  label: string
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${className}`}>
      {icon}
      {count} {label}
    </span>
  )
}
